'use client';

import { useTheme } from '../providers/ThemeProvider';

interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

// SVG Icons inline to avoid external dependencies
const SunIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </svg>
);

const MoonIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
  </svg>
);

const MonitorIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect width="20" height="14" x="2" y="3" rx="2" />
    <line x1="8" x2="16" y1="21" y2="21" />
    <line x1="12" x2="12" y1="17" y2="21" />
  </svg>
);

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

const buttonSizeClasses = {
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-2.5',
};

export function ThemeToggle({ className = '', size = 'md', showLabel = false }: ThemeToggleProps) {
  const { resolvedTheme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`
        inline-flex items-center gap-2 rounded-[var(--ids-radius-md,10px)] transition-colors
        ${buttonSizeClasses[size]}
        bg-[color:var(--ids-bg-subtle,#0F1626)] hover:bg-[color:var(--ids-surface-raised,#1A2336)]
        text-[color:var(--ids-text-muted,#94A3B8)] hover:text-[color:var(--ids-text,#F8FAFC)]
        ${className}
      `}
      title={resolvedTheme === 'dark' ? 'Przełącz na jasny motyw' : 'Przełącz na ciemny motyw'}
      aria-label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {resolvedTheme === 'dark' ? (
        <SunIcon className={sizeClasses[size]} />
      ) : (
        <MoonIcon className={sizeClasses[size]} />
      )}
      {showLabel && (
        <span className="text-sm font-medium">{resolvedTheme === 'dark' ? 'Jasny' : 'Ciemny'}</span>
      )}
    </button>
  );
}

// Advanced theme selector with all options
export function ThemeSelector({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useTheme();

  const options = [
    { value: 'light' as const, label: 'Jasny', icon: SunIcon },
    { value: 'dark' as const, label: 'Ciemny', icon: MoonIcon },
    { value: 'system' as const, label: 'System', icon: MonitorIcon },
  ];

  return (
    <div
      className={`flex items-center gap-1 p-1 rounded-[var(--ids-radius-md,10px)] bg-[color:var(--ids-bg-subtle,#0F1626)] border border-[color:var(--ids-border,rgba(255,255,255,0.08))] ${className}`}
    >
      {options.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          onClick={() => setTheme(value)}
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-ids-md text-sm font-semibold transition-colors
            ${
              theme === value
                ? 'bg-[color:rgba(var(--ids-brand-rgb,59,130,246),0.16)] text-[color:var(--ids-brand,#3B82F6)]'
                : 'text-[color:var(--ids-text-muted,#94A3B8)] hover:text-[color:var(--ids-text,#F8FAFC)]'
            }
          `}
          title={label}
        >
          <Icon className="w-4 h-4" />
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Gęstość interfejsu — `data-density` na `<html>`. Aurora ma trzy tryby od
 * pierwszej fali (`comfortable` 40px · `compact` 32px · `kiosk` 64px kontrolki),
 * ale do tej pory nic ich nie ustawiało, więc `compact` i `kiosk` były martwym
 * kodem. `kiosk` istnieje dla ekranów dotykowych na hali — patrz `KioskApp`.
 */
export function DensitySelector({ className = '' }: { className?: string }) {
  const { density, setDensity } = useTheme();

  const options = [
    { value: 'compact' as const, label: 'Zwarta' },
    { value: 'comfortable' as const, label: 'Wygodna' },
    { value: 'kiosk' as const, label: 'Kiosk' },
  ];

  return (
    <div className={`segmented ${className}`} role="group" aria-label="Gęstość interfejsu">
      {options.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => setDensity(value)}
          aria-pressed={density === value}
          className={density === value ? 'is-active' : undefined}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Wysoki kontrast — `data-contrast="high"` na `<html>`: mocniejsze obramowania
 * i grubszy pierścień focusu. Tokeny były w Aurorze od początku, ale nikt ich
 * nie włączał, więc tryb był niedostępny dla użytkownika.
 */
export function ContrastToggle({ className = '' }: { className?: string }) {
  const { contrast, setContrast } = useTheme();
  const high = contrast === 'high';

  return (
    <button
      type="button"
      onClick={() => setContrast(high ? 'normal' : 'high')}
      aria-pressed={high}
      className={`
        inline-flex items-center gap-2 rounded-[var(--ids-radius-md,10px)] p-2 transition-colors
        bg-[color:var(--ids-bg-subtle,#0F1626)] hover:bg-[color:var(--ids-surface-raised,#1A2336)]
        ${high ? 'text-[color:var(--ids-brand,#3B82F6)]' : 'text-[color:var(--ids-text-muted,#94A3B8)]'}
        ${className}
      `}
      title={high ? 'Wyłącz wysoki kontrast' : 'Włącz wysoki kontrast'}
      aria-label={high ? 'Wyłącz wysoki kontrast' : 'Włącz wysoki kontrast'}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="w-5 h-5"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3a9 9 0 0 0 0 18Z" fill="currentColor" stroke="none" />
      </svg>
    </button>
  );
}
